import { PerformanceReviewEditPageStep } from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import { PerformanceReviewEditSidebar } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewEditSidebar.tsx";
import {
  PerformanceReviewEditPageProps,
  usePerformanceReviewEditPage,
} from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import { PerformanceReviewTitleSlot } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewTitleSlot.tsx";
import { usePerformanceReviewUpdateReviewerTypeSettings } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import {
  ApiPerformanceReviewAutoShareTiming,
  ApiPerformanceReviewReviewerTypes,
  ApiPerformanceReviewUpdateReviewerTypeSettings,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Checkbox,
  FullScreenPage,
  Input,
  Select,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const isEditAfterSubmitGated = (setting?: {
  anonymous_threshold: number;
  is_auto_share: boolean;
  auto_share_timing: ApiPerformanceReviewAutoShareTiming | null;
}) =>
  (setting?.anonymous_threshold ?? 0) > 0 ||
  (!!setting?.is_auto_share &&
    setting?.auto_share_timing === ApiPerformanceReviewAutoShareTiming.immediately);

export const PerformanceReviewEditSettingsPage = ({ review }: PerformanceReviewEditPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = usePerformanceReviewEditPage();

  const updateSettings = usePerformanceReviewUpdateReviewerTypeSettings();

  const referenceOrder = PerformanceReviewReviewerTypeItems().map((item) => item.id);
  const reviewerTypes = review.reviewer_types.slice().sort((a, b) => {
    const indexA = referenceOrder.indexOf(a.type);
    const indexB = referenceOrder.indexOf(b.type);
    const safe = (i: number) => (i === -1 ? 999 : i);
    return safe(indexA) - safe(indexB);
  });

  const PerformanceReviewEditSettingsSchemaObject = yup.object({
    settings: yup
      .array()
      .of(
        yup.object({
          id: yup.number().required(t("errors:field_required")),
          setting_access: yup.object({
            is_allow_manager_early_access: yup.boolean().required(t("errors:field_required")),
            is_allow_manager_sharing: yup.boolean().required(t("errors:field_required")),
            is_allow_edit_after_submit: yup.boolean().nullable(),
          }),
          setting_notifications: yup.object({
            is_notify_owners_on_complete: yup.boolean().required(t("errors:field_required")),
          }),
          anonymous_threshold: yup.number().required(t("errors:field_required")),
          is_allow_to_invite: yup.boolean().required(t("errors:field_required")),
          is_allow_manager_to_invite: yup.boolean().default(false),
          is_allow_feedback_from_anyone: yup.boolean().default(false),
          is_auto_share: yup.boolean().required(t("errors:field_required")),
          auto_share_timing: yup.mixed<ApiPerformanceReviewAutoShareTiming>().nullable().defined(),
        })
      )
      .required(),
  });

  const { control, handleSubmit, setValue } =
    useForm<ApiPerformanceReviewUpdateReviewerTypeSettings>({
      mode: "onSubmit",
      resolver: yupResolver(PerformanceReviewEditSettingsSchemaObject),
      defaultValues: {
        settings: reviewerTypes.map((reviewer) => ({
          id: reviewer.id!,
          setting_access: {
            ...reviewer.setting_access,
            is_allow_edit_after_submit: reviewer.setting_access.is_allow_edit_after_submit ?? false,
          },
          setting_notifications: { ...reviewer.setting_notifications },
          anonymous_threshold: reviewer.anonymous_threshold,
          is_allow_to_invite: reviewer.is_allow_to_invite,
          is_allow_manager_to_invite: reviewer.is_allow_manager_to_invite ?? false,
          is_allow_feedback_from_anyone: reviewer.is_allow_feedback_from_anyone ?? false,
          is_auto_share: reviewer.is_auto_share ?? false,
          auto_share_timing: reviewer.auto_share_timing ?? null,
        })),
      },
    });

  const settingsValues = useWatch({ control, name: "settings" });
  const onSubmit = handleSubmit(async (data) => {
    const payload = {
      ...data,
      settings: data.settings.map((setting) =>
        isEditAfterSubmitGated(setting)
          ? {
              ...setting,
              setting_access: { ...setting.setting_access, is_allow_edit_after_submit: false },
            }
          : setting
      ),
    };
    try {
      await updateSettings.mutateAsync({
        id: review.id,
        payload,
      });

      navigateToStep(review.id, PerformanceReviewEditPageStep.invitation);
    } catch {}
  });

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(review.id)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigateToStep(review.id, PerformanceReviewEditPageStep.forms);
            }}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={onSubmit} isLoading={updateSettings.isPending}>
            {t("common:continue")}
          </Button>
        </>
      }
      titleSlot={
        <PerformanceReviewTitleSlot step={PerformanceReviewEditPageStep.settings} review={review} />
      }
      isCenterVertical={false}
      sidebarSlot={
        <PerformanceReviewEditSidebar
          step={PerformanceReviewEditPageStep.settings}
          review={review}
        />
      }
    >
      <Stack gap={"sm"} justify={"center"}>
        <Stack className={"card-panel"}>
          <Title
            header={t("performance_review:reviewers_settings")}
            caption={t("performance_review:reviewers_settings_description")}
            size={"lg"}
          />
        </Stack>

        {reviewerTypes.map((type, index) => {
          const showAllowManagerEarlyAccess =
            type.type === ApiPerformanceReviewReviewerTypes.myself;
          const showNotifyOwnersOnComplete =
            type.type === ApiPerformanceReviewReviewerTypes.manager;
          const showAllowToInvite = [
            ApiPerformanceReviewReviewerTypes.peer,
            ApiPerformanceReviewReviewerTypes.custom,
          ].includes(type.type);
          const showAllowToFeedbackFromColleague = [
            ApiPerformanceReviewReviewerTypes.peer,
            ApiPerformanceReviewReviewerTypes.custom,
          ].includes(type.type);

          const showSharingAccess = [
            ApiPerformanceReviewReviewerTypes.upward,
            ApiPerformanceReviewReviewerTypes.manager,
            ApiPerformanceReviewReviewerTypes.peer,
            ApiPerformanceReviewReviewerTypes.custom,
          ].includes(type.type);

          const showAutoShare = type.type !== ApiPerformanceReviewReviewerTypes.myself;

          const showAnonymous = [
            ApiPerformanceReviewReviewerTypes.upward,
            ApiPerformanceReviewReviewerTypes.peer,
            ApiPerformanceReviewReviewerTypes.custom,
          ].includes(type.type);
          const canEditAnonymous = true;

          const autoShareTimingOptions = [
            {
              id: ApiPerformanceReviewAutoShareTiming.after_end,
              title: t("performance_review:auto_share_timing_after_end"),
            },
            {
              id: ApiPerformanceReviewAutoShareTiming.immediately,
              title: t("performance_review:auto_share_timing_immediately"),
            },
          ];

          const currentSetting = settingsValues?.[index];

          const editAfterSubmitDisabled = isEditAfterSubmitGated(currentSetting);

          return (
            <Stack
              key={`reviewer-item-${type.type}-${type.id}`}
              className={"card-panel"}
              gap={"xl"}
            >
              <Title
                header={
                  PerformanceReviewReviewerTypeItems().find((x) => x.id == type.type)?.title ??
                  type.title ??
                  ""
                }
              />

              {showNotifyOwnersOnComplete && (
                <Stack gap={"sm"}>
                  <Title header={t("common:notifications")} size={"xs"} paddingBottom={true} />
                  {showNotifyOwnersOnComplete && (
                    <Controller
                      control={control}
                      name={`settings.${index}.setting_notifications.is_notify_owners_on_complete`}
                      render={({ field }) => (
                        <Checkbox
                          variant={"card"}
                          checked={field.value}
                          onChange={(checked) => field.onChange(checked)}
                          title={t("performance_review:settings_is_notify_owners_on_complete")}
                        />
                      )}
                    />
                  )}
                </Stack>
              )}

              {(showAllowManagerEarlyAccess || showSharingAccess || showAutoShare) && (
                <Stack gap={"sm"}>
                  <Title header={t("common:access")} size={"xs"} paddingBottom={true} />
                  {showSharingAccess && (
                    <Controller
                      control={control}
                      name={`settings.${index}.setting_access.is_allow_manager_sharing`}
                      render={({ field }) => (
                        <Checkbox
                          variant={"card"}
                          checked={field.value}
                          onChange={(checked) => field.onChange(checked)}
                          title={t("performance_review:settings_is_allow_manager_sharing")}
                        />
                      )}
                    />
                  )}

                  {showSharingAccess && (
                    <Controller
                      control={control}
                      name={`settings.${index}.setting_access.is_allow_edit_after_submit`}
                      render={({ field }) => (
                        <Checkbox
                          variant={"card"}
                          checked={editAfterSubmitDisabled ? false : !!field.value}
                          disabled={editAfterSubmitDisabled}
                          onChange={(checked) => field.onChange(checked)}
                          title={t("performance_review:settings_is_allow_edit_after_submit")}
                          caption={t(
                            "performance_review:settings_is_allow_edit_after_submit_description"
                          )}
                        />
                      )}
                    />
                  )}

                  {showAllowManagerEarlyAccess && (
                    <Controller
                      control={control}
                      name={`settings.${index}.setting_access.is_allow_manager_early_access`}
                      render={({ field }) => (
                        <Checkbox
                          variant={"card"}
                          checked={field.value}
                          onChange={(checked) => field.onChange(checked)}
                          title={t("performance_review:settings_is_allow_manager_early_access")}
                        />
                      )}
                    />
                  )}

                  {showAutoShare && (
                    <Controller
                      control={control}
                      name={`settings.${index}.is_auto_share`}
                      render={({ field: autoShareField }) => (
                        <Stack
                          gap={"sm"}
                          className={
                            "relative flex items-start gap-(--gap-sm) group bg-dark/5 border-dark/5 border px-4 py-2 rounded-xs"
                          }
                        >
                          <Checkbox
                            variant={"default"}
                            checked={autoShareField.value}
                            onChange={(checked) => {
                              autoShareField.onChange(checked);
                              if (checked && !currentSetting?.auto_share_timing) {
                                setValue(
                                  `settings.${index}.auto_share_timing`,
                                  ApiPerformanceReviewAutoShareTiming.after_end
                                );
                              } else if (!checked) {
                                setValue(`settings.${index}.auto_share_timing`, null);
                              }
                            }}
                            title={t("performance_review:settings_is_auto_share")}
                          />

                          {autoShareField.value && (
                            <Box className={"ml-7"}>
                              <Controller
                                control={control}
                                name={`settings.${index}.auto_share_timing`}
                                render={({ field: timingField }) => (
                                  <Select
                                    emptyTitle={""}
                                    variant={"button"}
                                    value={timingField.value}
                                    options={autoShareTimingOptions}
                                    onChange={(value) => {
                                      timingField.onChange(value);
                                    }}
                                  />
                                )}
                              />
                            </Box>
                          )}
                        </Stack>
                      )}
                    />
                  )}
                </Stack>
              )}

              {showAllowToInvite && (
                <Stack gap={"sm"}>
                  <Title header={t("common:invitation")} size={"xs"} paddingBottom={true} />
                  <Controller
                    control={control}
                    name={`settings.${index}.is_allow_to_invite`}
                    render={({ field }) => (
                      <Checkbox
                        variant={"card"}
                        checked={field.value}
                        onChange={(checked) => field.onChange(checked)}
                        title={t("performance_review:settings_is_allow_to_invite")}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`settings.${index}.is_allow_manager_to_invite`}
                    render={({ field }) => (
                      <Checkbox
                        variant={"card"}
                        checked={field.value}
                        onChange={(checked) => field.onChange(checked)}
                        title={t("performance_review:settings_is_allow_manager_to_invite")}
                      />
                    )}
                  />

                  {showAllowToFeedbackFromColleague && (
                    <Controller
                      control={control}
                      name={`settings.${index}.is_allow_feedback_from_anyone`}
                      render={({ field }) => (
                        <Checkbox
                          variant={"card"}
                          checked={field.value}
                          onChange={(checked) => field.onChange(checked)}
                          title={t("performance_review:settings_is_allow_feedback_from_anyone")}
                          caption={t(
                            "performance_review:settings_is_allow_feedback_from_anyone_description"
                          )}
                        />
                      )}
                    />
                  )}
                </Stack>
              )}

              {showAnonymous && (
                <Stack gap={"sm"}>
                  <Title
                    header={t("performance_review:settings_anonymity")}
                    size={"xs"}
                    paddingBottom={true}
                  />
                  <Controller
                    control={control}
                    name={`settings.${index}.anonymous_threshold`}
                    render={({ field }) => {
                      const isAnonymousEnabled = field.value !== 0;
                      return (
                        <Stack gap={"sm"}>
                          <Checkbox
                            variant={"card"}
                            checked={isAnonymousEnabled}
                            disabled={!canEditAnonymous}
                            onChange={() => {
                              field.onChange(isAnonymousEnabled ? 0 : 3);
                            }}
                            title={t("performance_review:settings_make_anonymous")}
                          />

                          {isAnonymousEnabled && (
                            <Input
                              label={t("performance_review:settings_anonymous_threshold")}
                              type="number"
                              disabled={!canEditAnonymous}
                              value={String(field.value)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = Number(e.target.value);
                                if (value >= 3) field.onChange(value);
                              }}
                            />
                          )}
                        </Stack>
                      );
                    }}
                  />
                </Stack>
              )}
            </Stack>
          );
        })}

        <Button onClick={onSubmit} size={"lg"} isLoading={updateSettings.isPending}>
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
};
